import { createSelector } from 'reselect'

import featureFlagsSelectorCreator from 'redux/selectors/feature_flags/feature_flags_selector'
import featureFlagsTypes from 'helpers/enums/featureFlagsTypes'
import { activitySelector } from 'redux/selectors/activities/'
import userRoleSelector from 'redux/selectors/user/user_role_selector'
import { maxUploadSizesByRole } from 'helpers/validation/upload_size'
import pickerSelector from 'redux/selectors/picker/picker_selector'
import volumeValues from 'helpers/enums/interactiveVideo/volumeValues'
import captionsSelector from 'redux/selectors/interactive_videos/captions_selector'

const interactiveVideoCreationSelector = state => state.interactiveVideoCreation

const interactiveVideoSettings = state => state.interactiveVideo.settings

const featureFlagsSelector = featureFlagsSelectorCreator([
	featureFlagsTypes.INTERACTIVE_VIDEO_YOUTUBE_SEARCH,
	featureFlagsTypes.INTERACTIVE_VIDEO_TRIMMING,
	featureFlagsTypes.INTERACTIVE_VIDEO_EDITOR_WELCOME_TOUR,
	featureFlagsTypes.INTERACTIVE_VIDEO_CONVERT_TO_SLIDES,
	featureFlagsTypes.INTERACTIVE_VIDEO_YOUTUBE_CAPTIONS,
	featureFlagsTypes.SPEEDS_PICKER_IN_VIDEO,
])

const InteractiveVideoCreateContainerSelector = createSelector(
	[
		interactiveVideoCreationSelector,
		featureFlagsSelector,
		activitySelector,
		userRoleSelector,
		pickerSelector,
		interactiveVideoSettings,
		captionsSelector,
	],
	(
		interactiveVideoData,
		featureFlagsData,
		activityData,
		userRole,
		pickerData,
		videoSettings,
		captions,
	) => {
		const mediaId = interactiveVideoData.media.asset ? interactiveVideoData.media.asset.uid : ''
		const posterId = interactiveVideoData.media.posterUid
		const updatedBreakpoint = interactiveVideoData.media.updatedBreakpoint
		const { trimTool } = interactiveVideoData

		const breakpoints = interactiveVideoData.media.breakpoints.map(breakpoint => {
			const isDisabled = trimTool.isActive
				&& (
					breakpoint.startTime > trimTool.trimEnd
					|| breakpoint.endTime < trimTool.trimStart
				)

			const activities = breakpoint.activities.map(
				activity => ({
					type: activityData(activity.activityUid).type,
					entity: activityData(activity.activityUid).entity,
					uid: activity.uid,
					order: activity.order,
				})
			)

			return {
				...breakpoint,
				uid: breakpoint.uid || breakpoint.clientId,
				activities,
				isDisabled,
			}

		})

		const isMuted = videoSettings.isMuted
		const maxUploadSize = maxUploadSizesByRole(userRole)

		return {
			updatedBreakpoint,
			mediaId,
			breakpoints,
			posterId,
			videoTitle: interactiveVideoData.settings.videoTitle,
			shouldShowYoutubeSearchBar: featureFlagsData[featureFlagsTypes.INTERACTIVE_VIDEO_YOUTUBE_SEARCH],
			shouldShowEmbedInSlidesButton: featureFlagsData[featureFlagsTypes.INTERACTIVE_VIDEO_CONVERT_TO_SLIDES],
			externalUid: interactiveVideoData.externalUid,
			maxUploadSize,
			googleDrivePickerOrigin: pickerData.googleDrivePickerOrigin,
			volume: isMuted ? volumeValues.MIN_VOLUME : videoSettings.volume,
			isMuted,
			shouldShowWelcomeTour: featureFlagsData[featureFlagsTypes.INTERACTIVE_VIDEO_EDITOR_WELCOME_TOUR],
			selectedPlaybackSpeed: videoSettings.selectedVideoPlaybackSpeed,
			selectedQuality: videoSettings.selectedVideoQuality,
			canUseTrimming: featureFlagsData[featureFlagsTypes.INTERACTIVE_VIDEO_TRIMMING],
			duration: interactiveVideoData.media.duration,
			trimStart: interactiveVideoData.media.trimStart,
			trimEnd: interactiveVideoData.media.trimEnd,
			trimTool: interactiveVideoData.trimTool,
			captions,
			canUseCaptions: featureFlagsData[featureFlagsTypes.INTERACTIVE_VIDEO_YOUTUBE_CAPTIONS],
			shouldShowSpeedsMenu: featureFlagsData[featureFlagsTypes.SPEEDS_PICKER_IN_VIDEO],
		}
	}
)

export default InteractiveVideoCreateContainerSelector
